import { useState, useEffect, useRef } from 'react';
import { analyzeRepairQuote, parseRepairQuoteResponse } from '../../services/autoAdvisorAI';
import { isNativeApp } from '../../utils/platform';
import Chat from '../chat/Chat';

const STAGES = [
  'Reading quote details...',
  'Researching fair prices...',
  'Comparing regional rates...',
  'Assessing repair scope...',
  'Building your report...',
];

/* ───────────────────── Mock data ───────────────────── */

function generateMockRepairResults(data) {
  const vehicleLabel = `${data.repairVehicleYear || ''} ${data.repairVehicleMake || ''} ${data.repairVehicleModel || ''}`.trim() || 'Your Vehicle';
  return {
    vehicleLabel,
    verdict: 'fair',
    verdictLabel: 'Fair Price',
    verdictNote: 'The quoted price is within the typical range for this repair in your area.',
    quotedTotal: data.repairTotalQuoted || 'Not specified',
    fairRange: '$900 – $1,400',
    sections: [
      {
        id: 'breakdown',
        title: 'Line Item Breakdown',
        emoji: '📋',
        items: [
          { label: 'Parts (Timing Belt Kit)', fairRange: '$180 – $280', quotedPrice: '$250', assessment: 'fair' },
          { label: 'Parts (Water Pump)', fairRange: '$80 – $150', quotedPrice: '$120', assessment: 'fair' },
          { label: 'Labor (4-6 hours)', fairRange: '$400 – $720', quotedPrice: '$580', assessment: 'fair' },
        ],
      },
      {
        id: 'questions',
        title: 'Questions to Ask',
        emoji: '❓',
        tips: [
          'Ask if OEM or aftermarket parts are being used — and if OEM, request the part numbers.',
          'Confirm if the quote includes the thermostat replacement (commonly done with timing belt).',
          'Ask about warranty on parts and labor — standard is 12 months/12,000 miles.',
          'Request an itemized breakdown if not already provided.',
        ],
      },
      {
        id: 'diy',
        title: 'DIY Assessment',
        emoji: '🔧',
        difficulty: 'Advanced',
        partsEstimate: '$200 – $350',
        timeEstimate: '6-8 hours',
        note: 'This is an advanced repair requiring special tools and timing procedure knowledge. Recommended for experienced DIYers only.',
      },
      {
        id: 'alternatives',
        title: 'Alternative Options',
        emoji: '💡',
        tips: [
          'Get 2-3 additional quotes from independent shops in your area.',
          'Check if the dealership offers competitive pricing for this specific service.',
          'Mobile mechanics may offer savings on labor for this type of repair.',
          'Ask about package deals that bundle related services (timing belt + water pump + thermostat).',
        ],
      },
    ],
  };
}

/* ───────────────────── Section renderers ───────────────────── */

function SectionContent({ section }) {
  if (section.id === 'breakdown' && section.items) {
    return (
      <>
        {section.items.map((item, i) => (
          <div key={i} style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 0',
            borderBottom: i < section.items.length - 1 ? '1px solid var(--border)' : 'none',
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                {item.label}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                Fair range: {item.fairRange}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                {item.quotedPrice}
              </div>
              <span style={{
                fontSize: '0.625rem',
                fontWeight: 600,
                padding: '2px 6px',
                borderRadius: 100,
                background: item.assessment === 'high'
                  ? 'rgba(239, 68, 68, 0.12)'
                  : item.assessment === 'low'
                    ? 'rgba(34, 197, 94, 0.12)'
                    : 'rgba(59, 130, 246, 0.12)',
                color: item.assessment === 'high'
                  ? '#ef4444'
                  : item.assessment === 'low'
                    ? '#22c55e'
                    : '#3b82f6',
                textTransform: 'uppercase',
              }}>
                {item.assessment}
              </span>
            </div>
          </div>
        ))}
      </>
    );
  }

  if (section.id === 'diy') {
    return (
      <>
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1, padding: '10px 12px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)' }}>
            <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Difficulty</div>
            <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)', marginTop: 2 }}>{section.difficulty}</div>
          </div>
          <div style={{ flex: 1, padding: '10px 12px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)' }}>
            <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Parts Cost</div>
            <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)', marginTop: 2 }}>{section.partsEstimate}</div>
          </div>
          <div style={{ flex: 1, padding: '10px 12px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)' }}>
            <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Time</div>
            <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)', marginTop: 2 }}>{section.timeEstimate}</div>
          </div>
        </div>
        <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          {section.note}
        </p>
      </>
    );
  }

  // Questions or Alternatives (tips-based sections)
  if (section.tips) {
    return (
      <>
        {section.tips.map((tip, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 10, fontSize: '0.8125rem' }}>
            <span style={{ color: 'var(--accent)', flexShrink: 0, fontWeight: 700 }}>{i + 1}.</span>
            <span style={{ color: 'var(--text-primary)', lineHeight: 1.5 }}>{tip}</span>
          </div>
        ))}
      </>
    );
  }

  return null;
}

function ResultSection({ section }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%',
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: '1.25rem' }}>{section.emoji}</span>
          <span style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{section.title}</span>
        </div>
        <svg
          width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{ transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0)', flexShrink: 0 }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {expanded && (
        <div style={{ padding: '0 20px 16px', borderTop: '1px solid var(--border)', paddingTop: 16, animation: 'fadeIn 0.2s ease' }}>
          <SectionContent section={section} />
        </div>
      )}
    </div>
  );
}

/* ───────────────────── Spinner styles ───────────────────── */

const spinnerContainerStyle = {
  width: 72, height: 72, margin: '0 auto 24px',
  position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const spinnerStyle = {
  position: 'absolute', inset: 0, borderRadius: '50%',
  border: '2.5px solid var(--border)', borderTopColor: 'var(--accent)',
  animation: 'spin 1s linear infinite',
};

/* ───────────────────── Main component ───────────────────── */

export default function RepairResults({ data, onReset, onExit }) {
  const [loading, setLoading] = useState(true);
  const [stageIndex, setStageIndex] = useState(0);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const abortRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current = controller;

    const interval = setInterval(() => {
      setStageIndex((prev) => (prev < STAGES.length - 1 ? prev + 1 : prev));
    }, 3000);

    async function run() {
      try {
        const onStage = (stage) => {
          console.log('[RepairAdvisor] Stage:', stage);
        };

        const rawJSON = await analyzeRepairQuote(data, controller.signal, onStage);
        if (controller.signal.aborted) return;

        const parsed = parseRepairQuoteResponse(rawJSON, data);
        setResults(parsed);
        setStageIndex(STAGES.length - 1);
        setLoading(false);
      } catch (err) {
        if (err.name === 'AbortError' || err.name === 'APIUserAbortError') return;
        console.error('[RepairAdvisor] Analysis failed:', err);

        // Show clear error messages for credit/rate limit issues
        if (err.message === 'NO_CREDITS') {
          setError('No credits remaining. Upgrade your plan for more.');
          setResults(generateMockRepairResults(data));
          setStageIndex(STAGES.length - 1);
          setLoading(false);
          return;
        }
        if (err.message === 'RATE_LIMITED') {
          setError('Too many requests. Please wait a minute before trying again.');
          setResults(generateMockRepairResults(data));
          setStageIndex(STAGES.length - 1);
          setLoading(false);
          return;
        }

        const mockResults = generateMockRepairResults(data);
        setResults(mockResults);
        setError(err.message || 'Analysis failed. Showing sample data.');
        setStageIndex(STAGES.length - 1);
        setLoading(false);
      }
    }

    run();

    return () => {
      controller.abort();
      clearInterval(interval);
    };
  }, [data]);

  /* ─── Loading phase ─── */
  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={spinnerContainerStyle}>
            <div style={spinnerStyle} />
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute' }}>
              <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />
            </svg>
          </div>

          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: 8 }}>Analyzing Quote</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9375rem', marginBottom: 32 }}>
            Checking fair prices for your repair
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, textAlign: 'left', maxWidth: 280 }}>
            {STAGES.map((label, i) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 12, opacity: i <= stageIndex ? 1 : 0.3, transition: 'opacity 0.4s' }}>
                <div style={{
                  width: 20, height: 20, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  background: i < stageIndex ? 'var(--success)' : i === stageIndex ? 'var(--accent)' : 'var(--bg-elevated)',
                  transition: 'background 0.3s',
                }}>
                  {i < stageIndex ? (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : i === stageIndex ? (
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'white', animation: 'pulse 1s infinite' }} />
                  ) : null}
                </div>
                <span style={{ fontSize: '0.875rem', color: i <= stageIndex ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>

        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
          @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        `}</style>
      </div>
    );
  }

  /* ─── Results phase ─── */
  const verdictColors = {
    low: { bg: 'rgba(34, 197, 94, 0.12)', color: '#22c55e', label: '✓ Great Price' },
    fair: { bg: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6', label: '~ Fair Price' },
    high: { bg: 'rgba(239, 68, 68, 0.12)', color: '#ef4444', label: '! Above Average' },
  };
  const vc = verdictColors[results.verdict] || verdictColors.fair;

  return (
    <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 200 }}>
      {/* Error banner */}
      {error && (
        <div style={{
          background: 'var(--bg-elevated)',
          border: `1px solid ${error.includes('credits') || error.includes('requests') ? 'var(--danger)' : 'var(--warning)'}`,
          borderRadius: 'var(--radius-sm)', padding: '10px 14px', marginBottom: 16,
          fontSize: '0.8125rem',
          color: error.includes('credits') || error.includes('requests') ? 'var(--danger)' : 'var(--warning)',
        }}>
          {error.includes('credits') || error.includes('requests')
            ? error
            : `AI analysis unavailable — showing sample data. (${error})`}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h1 className="screen-title">{results.vehicleLabel}</h1>
        <p className="screen-subtitle" style={{ marginTop: 4 }}>Repair Quote Analysis</p>
      </div>

      {/* Verdict card */}
      <div className="card" style={{ marginBottom: 12, textAlign: 'center', padding: '20px 24px' }}>
        <div style={{
          display: 'inline-block',
          padding: '6px 16px',
          borderRadius: 100,
          background: vc.bg,
          color: vc.color,
          fontWeight: 700,
          fontSize: '0.9375rem',
          marginBottom: 12,
        }}>
          {results.verdictLabel || vc.label}
        </div>
        <div style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: 4 }}>
          {results.quotedTotal}
        </div>
        <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 8 }}>
          Fair range: {results.fairRange}
        </div>
        {results.verdictNote && (
          <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.5, margin: 0 }}>
            {results.verdictNote}
          </p>
        )}
      </div>

      {/* Result sections */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {results.sections.map((section) => (
          <ResultSection key={section.id} section={section} />
        ))}
      </div>

      {/* Bottom CTAs */}
      <div style={{
        position: 'fixed', bottom: 60, left: '50%', transform: 'translateX(-50%)',
        width: '100%', maxWidth: 480, padding: '16px 20px 20px',
        background: 'linear-gradient(transparent, var(--bg-primary) 25%)',
      }}>
        {/* "Ask a question" is the new primary follow-up. "New Quote"
            stays available but demoted to secondary so we don't have
            two competing primary buttons. */}
        <button
          className="btn btn-primary"
          onClick={() => setShowChat(true)}
          style={{
            width: '100%',
            marginBottom: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          Ask a question
        </button>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={onReset} style={{ flex: 1 }}>
            New Quote
          </button>
        </div>
        <button className="btn btn-ghost" onClick={onExit} style={{ width: '100%', marginTop: 8 }}>
          Back to Main
        </button>
      </div>

      {/* Advisor follow-up chat overlay. Repair-mode system prompt
          loads the quote, line items, vehicle, and report findings so
          the assistant can argue specific items rather than the whole
          quote in aggregate. */}
      {showChat && (
        <Chat
          mode="advisor-repair"
          advisorData={{ ...data, results }}
          onBack={() => setShowChat(false)}
          onToast={() => { /* no-op */ }}
        />
      )}
    </div>
  );
}
